/**
 * Statically-typed registry of known feature-flag keys. Consumers gating UI
 * or routes should reference these constants (e.g. `FeatureFlags.CustomPositions`)
 * so typos fail at compile time.
 *
 * @remarks
 * This registry is intentionally for *developer*, build-time usage only.
 * The admin UI and data-plane APIs continue to work with plain `string` keys
 * because the server may return flags that haven't yet shipped in TypeScript.
 */
export const FeatureFlags = {
  CustomPositions: 'CustomPositions',
} as const;

/** Union of all known feature-flag keys. */
export type FeatureFlagKey = typeof FeatureFlags[keyof typeof FeatureFlags];

/**
 * Feature-flag registry entry as returned by the admin API.
 * Represents one row in the global feature-flag registry.
 */
export interface FeatureFlagRegistryEntry {
  readonly featureFlagId: string;
  readonly featureFlagKey: string;
  readonly displayName: string;
  readonly description: string | null;
  readonly defaultEnabled: boolean;
  readonly isActive: boolean;
  readonly createdUtc: string;
  readonly updatedUtc: string;
}

/**
 * Per-tenant feature-flag view returned by the admin API.
 * Combines the registry defaults with the tenant override (if any).
 */
export interface TenantFeatureFlagView {
  readonly featureFlagKey: string;
  readonly displayName: string;
  readonly description: string | null;
  readonly defaultEnabled: boolean;
  readonly isEnabled: boolean;
  readonly hasOverride: boolean;
}

/**
 * Payload for PUT /admin/tenants/{id}/feature-flags/{key}.
 * Sets a tenant-level override for a single flag.
 */
export interface TenantFeatureFlagUpdate {
  readonly isEnabled: boolean;
}
